import { useNavigate } from 'react-router-dom';
import { MinusCircle, PlusCircle, ShoppingCart } from 'lucide-react';
import Sheet from './Sheet';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

// Mirrors KFCStKitts/Views/Customer/CartView.swift presented as a
// .sheet with [.medium, .large] detents — here a single large detent.
export default function CartSheet({ open, onClose }) {
  const {
    items,
    updateQuantity,
    clearCart,
    getSubtotal,
    getChefServiceFee,
    getTotal,
    restaurantName,
    restaurantBannerURL,
  } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    navigate('/dashboard/cart');
  };

  const handleClearCart = () => {
    if (window.confirm('This will remove all items from your cart.')) {
      clearCart();
    }
  };

  return (
    <Sheet open={open} onClose={onClose} detent="large">
      {/* ChefHeader("Cart") equivalent */}
      <div className="sticky top-0 z-10 bg-white shadow-[0_2px_4px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-center px-4 py-3">
          <img
            src="/ChefLogo.jpg"
            alt="CheF SKN"
            className="absolute left-4 w-8 h-8 rounded-full object-cover ring-2 ring-[#FF6B35]"
          />
          <h2 className="text-base font-semibold text-gray-900">Cart</h2>
        </div>
      </div>

      {items.length === 0 ? (
        /* EmptyCartView */
        <div className="flex flex-col items-center justify-center py-20 px-6 gap-5">
          <ShoppingCart size={60} className="text-gray-400" strokeWidth={1.3} />
          <p className="text-xl font-medium text-gray-900">Your cart is empty</p>
          <p className="text-sm text-[#8E8E93] text-center -mt-3">
            Add some delicious items to get started
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-4 bg-[#F2F2F7]">
          {/* CartItemRow list */}
          {items.map((item) => (
            <CartItemRow
              key={`${item.id}-${item.variantName || 'base'}`}
              item={item}
              fallbackImage={restaurantBannerURL}
              onDecrement={() => updateQuantity(item.id, (item.quantity || 1) - 1, item.variantName || null)}
              onIncrement={() => updateQuantity(item.id, (item.quantity || 1) + 1, item.variantName || null)}
            />
          ))}

          {/* Order Summary card */}
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <h3 className="text-[17px] font-semibold text-gray-900">Order Summary</h3>
            {restaurantName && (
              <div className="flex justify-between text-[15px]">
                <span className="text-[#8E8E93]">Restaurant</span>
                <span className="text-gray-900 truncate ml-4">{restaurantName}</span>
              </div>
            )}
            <div className="flex justify-between text-[15px]">
              <span className="text-gray-900">Subtotal</span>
              <span className="text-gray-900">${getSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[15px]">
              <span className="text-gray-900">Chef Service Fee (2%)</span>
              <span className="text-gray-900">${getChefServiceFee().toFixed(2)}</span>
            </div>
            <div className="border-t border-black/[0.08]" />
            <div className="flex justify-between text-[15px] font-bold">
              <span className="text-gray-900">Total</span>
              <span className="text-gray-900">${getTotal().toFixed(2)}</span>
            </div>
          </div>

          {/* Proceed to Checkout */}
          <button
            onClick={handleCheckout}
            className="w-full bg-red-600 active:bg-red-700 text-white text-[17px] font-semibold py-3.5 rounded-xl transition-colors"
          >
            Proceed to Checkout
          </button>

          {/* Clear Cart — white card w/ red stroke (iOS destructive button) */}
          <button
            onClick={handleClearCart}
            className="w-full bg-white text-gray-900 text-[17px] font-semibold py-3.5 rounded-xl border border-[#FF3B30] active:bg-gray-50 transition-colors"
          >
            Clear Cart
          </button>
        </div>
      )}
    </Sheet>
  );
}

// Mirrors iOS CartItemRow: 80x80 image, name, "Cart item" caption,
// price + red minus/plus circle quantity controls.
function CartItemRow({ item, fallbackImage, onDecrement, onIncrement }) {
  const imageURL =
    item.menuItemImageURL ||
    item.imageURL ||
    item.restaurantBannerURL ||
    fallbackImage ||
    '/placeholder-food.jpg';

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 flex gap-3">
      <img
        src={imageURL}
        alt={item.name}
        className="w-20 h-20 rounded-lg object-cover bg-gray-200 shrink-0"
        onError={(e) => { e.target.src = '/placeholder-food.jpg'; }}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        <h4 className="text-[17px] font-bold text-gray-900 truncate">{item.name}</h4>
        <p className="text-[13px] text-[#8E8E93] line-clamp-2">
          {item.variantName ? `Size: ${item.variantName}` : 'Cart item'}
        </p>
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="text-[15px] font-semibold text-gray-900">
            ${(item.price || 0).toFixed(2)}
          </span>
          <div className="flex items-center gap-4">
            <button onClick={onDecrement} aria-label="Decrease quantity">
              <MinusCircle size={24} className="text-[#FF3B30] fill-[#FF3B30]/10" />
            </button>
            <span className="text-[17px] font-semibold text-gray-900 min-w-[20px] text-center">
              {item.quantity || 1}
            </span>
            <button onClick={onIncrement} aria-label="Increase quantity">
              <PlusCircle size={24} className="text-[#FF3B30] fill-[#FF3B30]/10" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
